import React, { Component, createContext, useContext } from "react";
import { apiUrl } from "../comp/ApiUrl";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Badge,
  Alert,
  Snackbar,
} from "@mui/material";

export const AuthContext = createContext();

export class AuthProvider extends Component {
  constructor(props) {
    super(props);

    // Verifica no localStorage se o áudio já foi liberado anteriormente
    const audioAlreadyAllowed = localStorage.getItem("audioAllowed") === "true";

    this.state = {
      user: null,
      loading: true,
      printerSocket: null,
      deliverySocket: null,
      isPrinterConnected: false,
      newOrdersCount: 0,
      audioAllowed: audioAlreadyAllowed,
      showAudioBanner: !audioAlreadyAllowed, // Exibe o banner apenas se ainda não ativou

      // DADOS DOS PEDIDOS RECEBIDOS VIA SSE EXPOSTOS AO CONTEXTO
      lastOrder: null,
      ordersList: [],
    };

    this.printerWs = null;
    this.eventSource = null;
    this.originalTitle = document.title;
    this.titleInterval = null;
    this.audioCtx = null;
    this.beepInterval = null;

    // GERENCIAMENTO DA IMPRESSORA LOCAL
    this.printerReconnectTimer = null;
    this.printQueue = [];
  }

  componentDidMount() {
    this.checkAuth();
    this.requestNotificationPermission();
  }

  componentWillUnmount() {
    this.closeWebSockets();
    this.stopAudioAlert();
    this.stopTitleBlink();
    if (this.printerReconnectTimer) {
      clearTimeout(this.printerReconnectTimer);
    }
  }

  // --- 1. NOTIFICAÇÕES NATIVAS DO SISTEMA OPERACIONAL ---
  requestNotificationPermission = () => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.permission === "default" && Notification.requestPermission();
    }
  };

  sendNativeNotification = (count, lastOrderData) => {
    if ("Notification" in window && Notification.permission === "granted") {
      const customerName =
        lastOrderData?.customerName || lastOrderData?.cliente || "Cliente";
      const notification = new Notification("📦 NOVO PEDIDO RECEBIDO!", {
        body: `Pedido de ${customerName}. Você tem ${count} pedido(s) aguardando confirmação.`,
        icon: "/favicon.ico",
        requireInteraction: true,
      });

      notification.onclick = () => {
        window.focus();
        this.dismissAllOrders();
      };
    }
  };

  // --- 2. SINTETIZADOR BEEP EM DUPLO TOM (WEB AUDIO API) ---
  initAudioContext = () => {
    if (!this.audioCtx) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioCtx = new AudioContext();
    }
    if (this.audioCtx.state === "suspended") {
      this.audioCtx.resume();
    }

    // Salva a permissão no localStorage para nunca mais exibir o banner
    localStorage.setItem("audioAllowed", "true");

    this.setState({
      audioAllowed: true,
      showAudioBanner: false,
    });
  };

  playBeepSound = () => {
    if (!this.audioCtx || this.audioCtx.state !== "running") return;

    try {
      const now = this.audioCtx.currentTime;

      // 1º Tom (Grave / C7 - 2093Hz)
      const osc1 = this.audioCtx.createOscillator();
      const gain1 = this.audioCtx.createGain();
      osc1.type = "sine";
      osc1.frequency.setValueAtTime(2093, now);
      gain1.gain.setValueAtTime(0.4, now);
      gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
      osc1.connect(gain1);
      gain1.connect(this.audioCtx.destination);
      osc1.start(now);
      osc1.stop(now + 0.12);

      // 2º Tom (Agudo / D#7 - 2528Hz)
      const osc2 = this.audioCtx.createOscillator();
      const gain2 = this.audioCtx.createGain();
      osc2.type = "sine";
      osc2.frequency.setValueAtTime(2528, now + 0.12);
      gain2.gain.setValueAtTime(0.5, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      osc2.connect(gain2);
      gain2.connect(this.audioCtx.destination);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.35);
    } catch (err) {
      console.error("Erro ao emitir o som do delivery:", err);
    }
  };

  triggerAudioAlert = () => {
    this.initAudioContext();
    this.stopAudioAlert();

    this.playBeepSound();
    this.beepInterval = setInterval(() => {
      this.playBeepSound();
    }, 1500);

    this.addInteractionListeners();
  };

  stopAudioAlert = () => {
    if (this.beepInterval) {
      clearInterval(this.beepInterval);
      this.beepInterval = null;
    }
    this.removeInteractionListeners();
  };

  // --- 3. PARAR ÁUDIO AO INTERAGIR COM A TELA ---
  addInteractionListeners = () => {
    this.removeInteractionListeners();
    window.addEventListener("mousemove", this.handleUserInteraction);
    window.addEventListener("click", this.handleUserInteraction);
    window.addEventListener("keydown", this.handleUserInteraction);
    window.addEventListener("touchstart", this.handleUserInteraction);
  };

  removeInteractionListeners = () => {
    window.removeEventListener("mousemove", this.handleUserInteraction);
    window.removeEventListener("click", this.handleUserInteraction);
    window.removeEventListener("keydown", this.handleUserInteraction);
    window.removeEventListener("touchstart", this.handleUserInteraction);
  };

  handleUserInteraction = () => {
    this.stopAudioAlert();
  };

  // --- 4. ANIMAR TÍTULO DA ABA DO NAVEGADOR ---
  startTitleBlink = (count) => {
    this.stopTitleBlink();
    let toggle = false;
    this.titleInterval = setInterval(() => {
      document.title = toggle
        ? `🚨 (${count}) NOVO PEDIDO!`
        : `📦 Pedidos Pendentes (${count})`;
      toggle = !toggle;
    }, 1000);
  };

  stopTitleBlink = () => {
    if (this.titleInterval) {
      clearInterval(this.titleInterval);
      this.titleInterval = null;
    }
    document.title = this.originalTitle;
  };

  // --- 5. MANIPULAÇÃO DE PEDIDOS RECEBIDOS ---
  handleNewOrder = (data) => {
    const isNotOnDeliveryPage = window.location.pathname !== "/delivery";

    this.setState(
      (prevState) => {
        const updatedCount = prevState.newOrdersCount + 1;
        return {
          newOrdersCount: isNotOnDeliveryPage ? updatedCount : 0,
          lastOrder: data,
          ordersList: [data, ...prevState.ordersList],
        };
      },
      () => {
        if (isNotOnDeliveryPage) {
          const count = this.state.newOrdersCount;
          this.triggerAudioAlert();
          this.startTitleBlink(count);
          this.sendNativeNotification(count, data);
        }
      }
    );
  };

  dismissAllOrders = () => {
    this.stopAudioAlert();
    this.stopTitleBlink();
    this.setState({ newOrdersCount: 0 });
  };

  // --- CONEXÃO SSE (SERVER-SENT EVENTS) ---
  initDeliverySSE = () => {
    const baseUrl = process.env.REACT_APP_API_URL || apiUrl;

    if (this.eventSource) {
      this.eventSource.close();
    }

    this.eventSource = new EventSource(`${baseUrl}/delivery/stream`, {
      withCredentials: true,
    });

    this.eventSource.onopen = () => {
      console.log("Conectado ao canal SSE de Delivery");
    };

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleNewOrder(data);
      } catch (err) {
        console.error("Erro ao ler evento SSE:", err);
      }
    };

    this.eventSource.addEventListener("new_order", (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleNewOrder(data);
      } catch (err) {
        console.error("Erro ao ler novo pedido SSE:", err);
      }
    });

    this.eventSource.onerror = () => {
      console.warn("Conexão SSE oscilou. Reconectando em 5s...");
      this.eventSource.close();
      setTimeout(() => {
        this.initDeliverySSE();
      }, 5000);
    };
  };

  // --- CONEXÃO WEBSOCKET IMPRESSORA COM RECONEXÃO E FILA ---
  initPrinterSocket = () => {
    if (this.printerReconnectTimer) {
      clearTimeout(this.printerReconnectTimer);
      this.printerReconnectTimer = null;
    }

    if (
      this.printerWs &&
      (this.printerWs.readyState === WebSocket.OPEN ||
        this.printerWs.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    try {
      const ws = new WebSocket("ws://127.0.0.1:41520");
      this.printerWs = ws;

      ws.onopen = () => {
        console.log("WebSocket Impressora Conectado!");
        this.setState({ printerSocket: ws, isPrinterConnected: true });
        ws.send(JSON.stringify({ type: "list_printers" }));
        this.flushPrintQueue();
      };

      ws.onclose = () => {
        this.setState({ printerSocket: null, isPrinterConnected: false });
        this.schedulePrinterReconnect();
      };

      ws.onerror = () => {
        this.setState({ printerSocket: null, isPrinterConnected: false });
      };
    } catch (err) {
      console.error("Erro WebSocket Impressora:", err);
      this.setState({ printerSocket: null, isPrinterConnected: false });
      this.schedulePrinterReconnect();
    }
  };

  schedulePrinterReconnect = () => {
    if (this.printerReconnectTimer) clearTimeout(this.printerReconnectTimer);
    this.printerReconnectTimer = setTimeout(() => {
      this.initPrinterSocket();
    }, 3000);
  };

  flushPrintQueue = () => {
    while (
      this.printQueue.length > 0 &&
      this.printerWs &&
      this.printerWs.readyState === WebSocket.OPEN
    ) {
      const payload = this.printQueue.shift();
      this.printerWs.send(JSON.stringify(payload));
      console.log("Pedido pendente enviado para a impressora:", payload);
    }
  };

  printData = (rawCommands, printName) => {
    const payload =
      typeof rawCommands === "string"
        ? { type: "print", printer: printName, data: rawCommands }
        : rawCommands;

    if (this.printerWs && this.printerWs.readyState === WebSocket.OPEN) {
      this.printerWs.send(JSON.stringify(payload));
      return true;
    }

    console.warn("Impressora offline. Adicionando pedido à fila de impressão...");
    this.printQueue.push(payload);
    this.initPrinterSocket();
    return false;
  };

  closeWebSockets = () => {
    if (this.printerReconnectTimer) {
      clearTimeout(this.printerReconnectTimer);
      this.printerReconnectTimer = null;
    }
    if (this.printerWs) {
      this.printerWs.close();
      this.printerWs = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.setState({
      printerSocket: null,
      isPrinterConnected: false,
    });
  };

  checkAuth = async () => {
    try {
      const response = await fetch(`${apiUrl}/auth/me`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        console.log('sou:')
        console.log(data)
        this.setState({ user: data.user }, () => {
          this.initPrinterSocket();
          this.initDeliverySSE();
        });
      } else {
        this.setState({ user: null });
      }
    } catch (error) {
      console.error("Erro na verificação de autenticação", error);
      this.setState({ user: null });
    } finally {
      this.setState({ loading: false });
    }
  };

  login = () => {
    window.location.reload();
  };

  logout = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || apiUrl;
      await fetch(`${baseUrl}/auth/logout`, { credentials: "include" });
    } catch (error) {
      console.error("Erro no logout", error);
    } finally {
      this.closeWebSockets();
      this.stopAudioAlert();
      this.stopTitleBlink();
      this.setState({ user: null });
    }
  };

  render() {
    const {
      user,
      loading,
      printerSocket,
      deliverySocket,
      isPrinterConnected,
      newOrdersCount,
      showAudioBanner,
      lastOrder,
      ordersList,
    } = this.state;

    const value = {
      isAuthenticated: !!user,
      user,
      login: this.login,
      logout: this.logout,
      loading,
      socket: deliverySocket,
      printerSocket,
      printData: this.printData,
      isPrinterConnected,
      initAudioContext: this.initAudioContext,
      newOrdersCount,
      lastOrder,
      ordersList,
      dismissAllOrders: this.dismissAllOrders,
    };

    return (
      <AuthContext.Provider value={value}>
        {showAudioBanner && user && (
          <Snackbar
            open={showAudioBanner}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            onClose={() => this.setState({ showAudioBanner: false })}
          >
            <Alert
              severity="warning"
              onClose={() => this.setState({ showAudioBanner: false })}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={this.initAudioContext}
                  sx={{ fontWeight: "bold" }}
                >
                  ATIVAR SOM
                </Button>
              }
            >
              Clique para ativar os alertas sonoros de novos pedidos.
            </Alert>
          </Snackbar>
        )}

        {!loading && this.props.children}

        <Dialog
          open={newOrdersCount > 0}
          onClose={() => { }}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            style: { borderRadius: 12, padding: "4px" },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1,
            }}
          >
            <Typography variant="h6" component="span" fontWeight="bold" color="text.primary">
              {newOrdersCount > 1 ? "Novos Pedidos" : "Novo Pedido"}
            </Typography>
            <Badge badgeContent={newOrdersCount} color="error" sx={{ mr: 2 }} />
          </DialogTitle>

          <DialogContent sx={{ py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Você tem <strong>{newOrdersCount}</strong>{" "}
              {newOrdersCount === 1
                ? "novo pedido aguardando"
                : "novos pedidos aguardando"}{" "}
              no painel de delivery.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <Button
              onClick={this.dismissAllOrders}
              color="inherit"
              size="small"
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Silenciar Alerta
            </Button>
            <Button
              onClick={() => {
                this.dismissAllOrders();
                window.location.href = "/delivery";
              }}
              variant="contained"
              color="primary"
              size="small"
              sx={{ borderRadius: 2, fontWeight: "bold", textTransform: "none" }}
            >
              Ver Delivery
            </Button>
          </DialogActions>
        </Dialog>
      </AuthContext.Provider>
    );
  }
}

export const useAuth = () => useContext(AuthContext);